import React, { useState, useEffect } from 'react';
import { PAIEMENT_ACTIF } from '../config/features';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '../i18n/router';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
  Avatar,
} from '@mui/material';
import { useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { recupererListeVoyageurs, checkUserSociete } from '../Services';
import AssuranceVisaComponent from '../components/AssuranceVisaComponent';
import { Voyageurs } from '../models/Voyageurs';
import { API_BASE_URL } from '../config/api';

interface FormScreenRouteParams {
  title: string;
  prixconsulat: number;
  prixintervention: number;
  demandeId: number;
  url_flag: string;
  validite: string;
  informations: string;
  commentaires?: string;
  titleDisplay?: string;
}

const FormBurkinaFaso: React.FC = () => {
  const { t } = useTranslation('formulaires');
  const { t: tp } = useTranslation('formulaires_pays');
  // Récupération des paramètres passés via react-router
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromVoyageurCreation = searchParams.get('fromCreation') === 'true';

  // Essayer de récupérer le state depuis location.state ou sessionStorage
  const getInitialState = (): FormScreenRouteParams => {
    // Si on revient de la création de voyageur, utiliser sessionStorage
    if (fromVoyageurCreation) {
      const saved = sessionStorage.getItem('formState');
      if (saved) {
        return JSON.parse(saved) as FormScreenRouteParams;
      }
    }
    // Sinon utiliser location.state et nettoyer sessionStorage
    if (location.state && Object.keys(location.state).length > 0) {
      sessionStorage.removeItem('formState');
      return location.state as FormScreenRouteParams;
    }
    return {} as FormScreenRouteParams;
  };
  const state = getInitialState();
  const titleDisplay = (state as any)?.titleDisplay || (state as any)?.title || '';
  const demandeId = state.demandeId;
  const isEditMode = demandeId != null;

  // États issus des paramètres ou valeurs par défaut
  const [title] = useState<string>(state?.title || '');
  const [prixconsulat] = useState<number>(state?.prixconsulat || 0);
  const [prixintervention] = useState<number>(state?.prixintervention || 0);
  const [urlFlag] = useState<string>(state?.url_flag || '');
  const [validite] = useState<string>(state?.validite || '');
  const commentaires = state?.commentaires || '';
  const [informations] = useState<string>(state?.informations || '');

  // Champs communs
  const [dateDepart, setDateDepart] = useState<string>('');
  const [dateSortie, setDateSortie] = useState<string>('');
  const [voyageurs, setVoyageurs] = useState<Voyageurs[]>([]);
  const [selectedVoyageur, setSelectedVoyageur] = useState<string>('');
  const [champsNonRemplis, setChampsNonRemplis] = useState<string[]>([]);
  const [assuranceVisa, setAssuranceVisa] = useState<string>('');
  const [sliderValue, setSliderValue] = useState<boolean>(false);
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hideFees] = useState<boolean>(false);
  const [isClientSociete, setIsClientSociete] = useState<boolean | null>(null);
  const [showSuccessScreen, setShowSuccessScreen] = useState<boolean>(false);
  const [createdDemandeId, setCreatedDemandeId] = useState<number | null>(null);

  // Nombre d'enfants
  const [nombreEnfants, setNombreEnfants] = useState<string>('');

  // Voyage
  const [motifVoyage, setMotifVoyage] = useState<string>('');
  const [dureeSejour, setDureeSejour] = useState<string>('');
  const [adresseSejour, setAdresseSejour] = useState<string>('');
  const [pointEntree, setPointEntree] = useState<string>('');

  // Hébergement
  const [nomHebergement, setNomHebergement] = useState<string>('');
  const [adresseHebergement, setAdresseHebergement] = useState<string>('');

  // Contact d'urgence
  const [contactUrgenceNom, setContactUrgenceNom] = useState<string>('');
  const [contactUrgenceTelephone, setContactUrgenceTelephone] = useState<string>('');
  const [contactUrgenceEmail, setContactUrgenceEmail] = useState<string>('');

  // Informations complémentaires
  const [employeur, setEmployeur] = useState<string>('');
  const [moyensFinanciers, setMoyensFinanciers] = useState<string>('');

  // Documents à télécharger
  const [carnetVaccinationFile, setCarnetVaccinationFile] = useState<File | null>(null);
  const [assuranceFile, setAssuranceFile] = useState<File | null>(null);
  const [billetAvionFile, setBilletAvionFile] = useState<File | null>(null);
  const [reservationHotelFile, setReservationHotelFile] = useState<File | null>(null);

  const optionsMotif = [
    { value: 'tourisme', label: tp('burkinaFaso.motifOptions.tourisme') },
    { value: 'famille', label: tp('burkinaFaso.motifOptions.famille') },
    { value: 'affaires', label: tp('burkinaFaso.motifOptions.affaires') },
    { value: 'autre', label: tp('burkinaFaso.motifOptions.autre') },
  ];

  // Calcul du total
  const total = prixconsulat + prixintervention + (assuranceVisa.toLowerCase() === 'oui' ? 20 : 0);

  // Fonction utilitaire pour parser une date au format jj/mm/aaaa
  const parseDate = (dateStr: string): Date | null => {
    const parts = dateStr.split('/');
    if (parts.length === 3) {
      return new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
    }
    return null;
  };

  // Validation du formulaire
  const validateForm = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const arriveeDate = parseDate(dateDepart);
    const errors: string[] = [];

    // Voyageur
    if (!selectedVoyageur) errors.push(t('common.validation.travelerNotSelected'));

    // Date d'arrivée
    if (arriveeDate) {
      if (isNaN(arriveeDate.getTime()) || arriveeDate <= today) {
        errors.push(tp('burkinaFaso.errors.arrivalDateInvalid'));
      }
    } else {
      errors.push(tp('burkinaFaso.errors.arrivalDateRequired'));
    }

    // Voyage
    if (!motifVoyage) errors.push(tp('burkinaFaso.validation.motifVoyage'));
    if (!dureeSejour.trim()) errors.push(tp('burkinaFaso.validation.dureeSejour'));
    if (!adresseSejour.trim()) errors.push(tp('burkinaFaso.validation.adresseSejour'));
    if (!pointEntree.trim()) errors.push(tp('burkinaFaso.validation.pointEntree'));

    // Hébergement
    if (!nomHebergement.trim()) errors.push(tp('burkinaFaso.validation.nomHebergement'));
    if (!adresseHebergement.trim()) errors.push(tp('burkinaFaso.validation.adresseHebergement'));

    // Contact d'urgence
    if (!contactUrgenceNom.trim()) errors.push(tp('burkinaFaso.validation.contactUrgenceNom'));
    if (!contactUrgenceTelephone.trim()) errors.push(tp('burkinaFaso.validation.contactUrgenceTelephone'));

    // Assurance + CGV
    if (!assuranceVisa.trim()) errors.push(t('common.validation.insurance'));
    if (!sliderValue) errors.push(t('common.validation.cguAcceptance'));

    setChampsNonRemplis(errors);
    setIsFormValid(errors.length === 0);
  };

  useEffect(() => {
    validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dateDepart,
    selectedVoyageur,
    motifVoyage,
    dureeSejour,
    adresseSejour,
    pointEntree,
    nomHebergement,
    adresseHebergement,
    contactUrgenceNom,
    contactUrgenceTelephone,
    assuranceVisa,
    sliderValue,
  ]);

  // Récupération de la liste des voyageurs
  useEffect(() => {
    const fetchVoyageurs = async () => {
      const userJson = localStorage.getItem('user');
      if (userJson) {
        const user = JSON.parse(userJson);
        const listeVoyageurs = await recupererListeVoyageurs(user.id_utilisateur);
        setVoyageurs(listeVoyageurs);
        // Si un seul voyageur, sélection automatique
        if (listeVoyageurs.length === 1) {
          setSelectedVoyageur(listeVoyageurs[0].id.toString());
        } else {
          setSelectedVoyageur('');
        }
      }
    };
    fetchVoyageurs();
  }, [demandeId]);

  // Vérification si l'utilisateur appartient à une société
  useEffect(() => {
    const checkUserStatus = async () => {
      try {
        const userJson = localStorage.getItem('user');
        if (userJson) {
          const user = JSON.parse(userJson);
          const societeResult = await checkUserSociete(user.id_utilisateur);
          setIsClientSociete(societeResult.isSociete);
        }
      } catch (error) {
        console.error('Erreur lors de la vérification du statut utilisateur:', error);
        setIsClientSociete(true); // Par défaut, considérer comme société
      }
    };
    checkUserStatus();
  }, []);

  const selectedVoyageurObj = voyageurs.find(v => v.id.toString() === selectedVoyageur);

  // Empêcher la fermeture accidentelle de l'onglet
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // Bouton retour
  const handleBack = () => {
    if (window.confirm(t('common.confirmation.unsavedChanges'))) {
      navigate(-1);
    }
  };

  // Formatage de la date en jj/mm/aaaa
  const formatDate = (input: string) => {
    const numbers = input.replace(/[^\d]/g, '');
    let formatted = '';
    for (let i = 0; i < numbers.length && i < 8; i++) {
      if (i === 2 || i === 4) {
        formatted += '/';
      }
      formatted += numbers[i];
    }
    return formatted;
  };

  // Gestion des uploads
  const handleFileChange =
    (setter: React.Dispatch<React.SetStateAction<File | null>>) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files && e.target.files[0];
      if (file) {
        setter(file);
      }
    };

  // Soumission du formulaire
  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const userJson = localStorage.getItem('user');
      let userId: string | null = null;
      if (userJson) {
        const user = JSON.parse(userJson);
        userId = user.id_utilisateur;
      }

      const etatFormulaire = champsNonRemplis.length === 0;

      // Construction du FormData (fichiers + champs texte)
      const formData = new FormData();
      formData.append('etatFormulaire', String(etatFormulaire));
      formData.append('idUtilisateur', userId || '');
      formData.append('idVoyageur', selectedVoyageur);
      formData.append('titreVisa', title);
      formData.append('dateDepart', dateDepart);
      formData.append('dateSortie', dateSortie);
      formData.append('validite', validite);
      formData.append('assuranceVisa', assuranceVisa);
      formData.append('prixconsulat', String(prixconsulat));
      formData.append('prixintervention', String(prixintervention));

      // Voyage
      formData.append('motifVoyage', motifVoyage);
      formData.append('dureeSejour', dureeSejour);
      formData.append('adresseSejour', adresseSejour);
      formData.append('pointEntree', pointEntree);

      // Hébergement
      formData.append('nomHebergement', nomHebergement);
      formData.append('adresseHebergement', adresseHebergement);

      // Contact d'urgence
      formData.append('contactUrgenceNom', contactUrgenceNom);
      formData.append('contactUrgenceTelephone', contactUrgenceTelephone);
      formData.append('contactUrgenceEmail', contactUrgenceEmail);

      // Informations complémentaires
      formData.append('nombreEnfants', nombreEnfants);
      formData.append('employeur', employeur);
      formData.append('moyensFinanciers', moyensFinanciers);

      // Documents
      if (carnetVaccinationFile) formData.append('carnetVaccinationFile', carnetVaccinationFile);
      if (assuranceFile) formData.append('assuranceFile', assuranceFile);
      if (billetAvionFile) formData.append('billetAvionFile', billetAvionFile);
      if (reservationHotelFile) formData.append('reservationHotelFile', reservationHotelFile);

      const response = await axios.post(`${API_BASE_URL}/validerDemande`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setIsLoading(false);

      // Stocker l'ID de la demande créée si disponible
      if (response && response.data && response.data.idDemande) {
        setCreatedDemandeId(response.data.idDemande);
      }

      // Afficher l'écran de succès au lieu de naviguer directement
      setShowSuccessScreen(true);
    } catch (error) {
      console.error('Erreur lors de la validation de la demande:', error);
      setIsLoading(false);
    }
  };

  // Navigation vers le paiement SumUp (pour particuliers hors société)
  const handlePayerEnLigne = () => {
    if (createdDemandeId) {
      navigate('/paiement', {
        state: {
          bonId: 0,
          urlPaiement: '',
          demandeIds: [createdDemandeId],
          modePaiementSumup: true,
        },
      });
    } else {
      navigate('/retour', { state: { tabValue: 2, showPaymentMessage: true } });
    }
  };

  // Créer une nouvelle demande (réinitialiser le formulaire)
  const handleNouvelleDemande = () => {
    setShowSuccessScreen(false);
    setCreatedDemandeId(null);
    setDateDepart('');
    setDateSortie('');
    setSelectedVoyageur(voyageurs.length === 1 ? voyageurs[0].id.toString() : '');
    setAssuranceVisa('');
    setSliderValue(false);
    setNombreEnfants('');
    setMotifVoyage('');
    setDureeSejour('');
    setAdresseSejour('');
    setPointEntree('');
    setNomHebergement('');
    setAdresseHebergement('');
    setContactUrgenceNom('');
    setContactUrgenceTelephone('');
    setContactUrgenceEmail('');
    setEmployeur('');
    setMoyensFinanciers('');
    setCarnetVaccinationFile(null);
    setAssuranceFile(null);
    setBilletAvionFile(null);
    setReservationHotelFile(null);
  };

  const disabledPressHandler = () => {};

  // Rendu d'un champ d'upload de fichier
  const renderFileInput = (
    label: string,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void,
    file: File | null
  ) => (
    <Box sx={{ my: 2 }}>
      <Typography variant="subtitle1">{label}</Typography>
      <input type="file" accept="image/*,application/pdf" onChange={onChange} />
      {file && (
        <Typography variant="caption" sx={{ display: 'block', color: 'green', mt: 0.5 }}>
          {file.name}
        </Typography>
      )}
    </Box>
  );

  // En-tête de section
  const renderSectionTitle = (label: string) => (
    <Box sx={{ mt: 3, mb: 1 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, color: '#333' }}>
        {label}
      </Typography>
      <Divider sx={{ mt: 0.5 }} />
    </Box>
  );

  // Si l'écran de succès est affiché
  if (showSuccessScreen) {
    return (
      <Box sx={{ backgroundColor: '#ECE4D3', minHeight: '100vh', py: 4 }}>
        <Container maxWidth="md">
          <Box sx={{ backgroundColor: '#fff', p: 4, borderRadius: 2, boxShadow: 2, textAlign: 'center' }}>
            {/* En-tête avec drapeau */}
            {urlFlag && (
              <Box
                component="img"
                src={urlFlag}
                alt={t('common.alt.flag')}
                sx={{ width: 60, height: 36, mb: 2, mx: 'auto' }}
              />
            )}

            {/* Message de succès */}
            <Typography variant="h4" sx={{ color: 'green', fontWeight: 'bold', mb: 2 }}>
              {t('success.title')}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {t('success.message', { title: titleDisplay })}
            </Typography>

            {/* Boutons conditionnels selon le type d'utilisateur */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center', mt: 4 }}>
              {/* Bouton Payer en ligne - uniquement pour particuliers (hors société) */}
              {isClientSociete === false && !hideFees && (
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handlePayerEnLigne}
                  sx={{ minWidth: 280 }}
                >
                  {PAIEMENT_ACTIF ? t('success.payOnline') : t('success.launchDemande')}
                </Button>
              )}

              {/* Bouton Créer une nouvelle demande - pour tous les utilisateurs */}
              <Button
                variant="outlined"
                color="primary"
                size="large"
                onClick={handleNouvelleDemande}
                sx={{ minWidth: 280 }}
              >
                {t('success.newRequest')}
              </Button>

              {/* Bouton Voir mes demandes */}
              <Button
                variant="text"
                color="inherit"
                onClick={() => navigate('/retour', { state: { tabValue: 2 } })}
                sx={{ mt: 1 }}
              >
                {t('success.viewRequests')}
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box component="form" noValidate autoComplete="off" sx={{ backgroundColor: '#ECE4D3', minHeight: '100vh', py: 4 }}>
      <Container maxWidth="md">
        <Box sx={{ backgroundColor: '#fff', p: 3, borderRadius: 2, boxShadow: 2 }}>
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            {urlFlag && (
              <Box
                component="img"
                src={urlFlag}
                alt={t('common.alt.flag')}
                sx={{ width: 50, height: 30, mb: 1, mx: 'auto' }}
              />
            )}
            <Typography variant="h5" component="h1" gutterBottom>
              {t('common.formTitle', { title: titleDisplay })}
            </Typography>
            <Typography variant="body1" gutterBottom>
              {validite}
              <br />
              {informations}
            </Typography>
            <Button variant="outlined" onClick={handleBack} sx={{ mt: 1 }}>
              {t('common.back')}
            </Button>
          </Box>

          {/* Affichage des commentaires du visa en HTML */}
          {commentaires && (
            <Box
              sx={{
                mb: 2,
                p: 2,
                backgroundColor: '#fff3cd',
                borderRadius: 1,
                border: '1px solid #ffc107',
              }}
              dangerouslySetInnerHTML={{ __html: commentaires }}
            />
          )}

          {/* Sélection du voyageur */}
          {!isEditMode && (
            <>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="voyageur-select-label">{t('common.selectTraveler')}</InputLabel>
                <Select
                  labelId="voyageur-select-label"
                  id="voyageur-select"
                  value={selectedVoyageur}
                  label={t('common.selectTraveler')}
                  onChange={(e: SelectChangeEvent) => setSelectedVoyageur(e.target.value)}
                >
                  {voyageurs.map(v => (
                    <MenuItem key={v.id} value={v.id.toString()}>
                      {v.nom} {v.prenom}
                      {v.numeropax ? t('common.selectTravelerWithPassport', { passport: v.numeropax }) : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Bouton pour créer un nouveau voyageur */}
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    sessionStorage.setItem('formState', JSON.stringify({
                      title,
                      prixconsulat,
                      prixintervention,
                      url_flag: urlFlag,
                      validite,
                      informations,
                      commentaires
                    }));
                    navigate(`/formulaire_creation_voyageur?returnUrl=${location.pathname}`);
                  }}
                  sx={{
                    color: '#D89D2F',
                    borderColor: '#D89D2F',
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                      borderColor: '#B5811F'
                    }
                  }}
                >
                  {t('common.createNewTraveler')}
                </Button>
              </Box>
            </>
          )}

          {selectedVoyageurObj && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Avatar
                src={selectedVoyageurObj.url || 'https://example.com/default-avatar.png'}
                alt={t('common.alt.avatar')}
                sx={{ width: 100, height: 100 }}
              />
            </Box>
          )}

          {/* ===== Voyage ===== */}
          {renderSectionTitle(tp('burkinaFaso.sections.voyage'))}

          <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
            <InputLabel id="motif-label">{tp('burkinaFaso.fields.motifVoyage')}</InputLabel>
            <Select
              labelId="motif-label"
              id="motif-select"
              value={motifVoyage}
              label={tp('burkinaFaso.fields.motifVoyage')}
              onChange={(e) => setMotifVoyage(e.target.value)}
            >
              {optionsMotif.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label={tp('burkinaFaso.arrivalDate.label')}
            placeholder={t('common.departureDate.placeholder')}
            value={dateDepart}
            onChange={(e) => setDateDepart(formatDate(e.target.value))}
            fullWidth
            margin="normal"
          />
          <TextField
            label={t('common.returnDate.shortLabel')}
            placeholder={t('common.returnDate.placeholder')}
            value={dateSortie}
            onChange={(e) => setDateSortie(formatDate(e.target.value))}
            fullWidth
            margin="normal"
          />
          <TextField
            label={tp('burkinaFaso.fields.dureeSejour')}
            value={dureeSejour}
            onChange={(e) => setDureeSejour(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label={tp('burkinaFaso.fields.adresseSejour')}
            value={adresseSejour}
            onChange={(e) => setAdresseSejour(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label={tp('burkinaFaso.fields.pointEntree')}
            value={pointEntree}
            onChange={(e) => setPointEntree(e.target.value)}
            fullWidth
            margin="normal"
          />

          {/* ===== Hébergement ===== */}
          {renderSectionTitle(tp('burkinaFaso.sections.hebergement'))}

          <TextField
            label={tp('burkinaFaso.fields.nomHebergement')}
            value={nomHebergement}
            onChange={(e) => setNomHebergement(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label={tp('burkinaFaso.fields.adresseHebergement')}
            value={adresseHebergement}
            onChange={(e) => setAdresseHebergement(e.target.value)}
            fullWidth
            margin="normal"
          />

          {/* ===== Contact en cas d'urgence ===== */}
          {renderSectionTitle(tp('burkinaFaso.sections.contactUrgence'))}

          <TextField
            label={tp('burkinaFaso.fields.contactUrgenceNom')}
            value={contactUrgenceNom}
            onChange={(e) => setContactUrgenceNom(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label={tp('burkinaFaso.fields.contactUrgenceTelephone')}
            value={contactUrgenceTelephone}
            onChange={(e) => setContactUrgenceTelephone(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label={tp('burkinaFaso.fields.contactUrgenceEmail')}
            type="email"
            value={contactUrgenceEmail}
            onChange={(e) => setContactUrgenceEmail(e.target.value)}
            fullWidth
            margin="normal"
          />

          {/* ===== Informations complémentaires ===== */}
          {renderSectionTitle(tp('burkinaFaso.sections.complementaires'))}

          <TextField
            label={tp('burkinaFaso.fields.nombreEnfants')}
            value={nombreEnfants}
            onChange={(e) => setNombreEnfants(e.target.value.replace(/[^\d]/g, ''))}
            fullWidth
            margin="normal"
          />
          <TextField
            label={tp('burkinaFaso.fields.employeur')}
            value={employeur}
            onChange={(e) => setEmployeur(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label={tp('burkinaFaso.fields.moyensFinanciers')}
            value={moyensFinanciers}
            onChange={(e) => setMoyensFinanciers(e.target.value)}
            fullWidth
            margin="normal"
            multiline
            minRows={2}
          />

          {/* ===== Documents à télécharger ===== */}
          {renderSectionTitle(tp('burkinaFaso.sections.documents'))}

          {renderFileInput(tp('burkinaFaso.fields.carnetVaccination'), handleFileChange(setCarnetVaccinationFile), carnetVaccinationFile)}
          {renderFileInput(tp('burkinaFaso.fields.assurance'), handleFileChange(setAssuranceFile), assuranceFile)}
          {renderFileInput(tp('burkinaFaso.fields.billetAvion'), handleFileChange(setBilletAvionFile), billetAvionFile)}
          {renderFileInput(tp('burkinaFaso.fields.reservationHotel'), handleFileChange(setReservationHotelFile), reservationHotelFile)}

          {/* Module Assurance */}
          <Box sx={{ my: 2 }}>
            <AssuranceVisaComponent
              assuranceVisa={assuranceVisa}
              setAssuranceVisa={setAssuranceVisa}
              sliderValue={sliderValue}
              setSliderValue={setSliderValue}
            />
          </Box>

          {/* Récapitulatif du total */}
          {!hideFees && (
            <Box sx={{ textAlign: 'center', my: 2 }}>
              <Typography variant="body1">{t('common.fees.visa', { prix: prixconsulat })}</Typography>
              <Typography variant="body1">{t('common.fees.service', { prix: prixintervention })}</Typography>
              <Typography variant="h6" sx={{ mt: 1 }}>
                {t('common.fees.total', { total })}
              </Typography>
            </Box>
          )}

          {/* État du formulaire */}
          <Box
            sx={{
              my: 2,
              p: 2,
              borderRadius: 1,
              backgroundColor: champsNonRemplis.length > 0 ? '#ffcccb' : '#ccffcc',
            }}
          >
            {champsNonRemplis.length > 0 ? (
              <>
                <Typography variant="h6" align="center">
                  {t('common.formStatus.incomplete')}
                </Typography>
                {champsNonRemplis.map((champ, index) => (
                  <Typography key={index}>{champ}</Typography>
                ))}
              </>
            ) : (
              <Typography align="center">{t('common.formStatus.complete')}</Typography>
            )}
          </Box>

          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', my: 2 }}>
              <Button
                variant="contained"
                onClick={isFormValid ? handleSubmit : disabledPressHandler}
                disabled={!isFormValid}
                sx={{ backgroundColor: isFormValid ? 'black' : '#A9A9A9' }}
              >
                {t('common.submit')}
              </Button>
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default FormBurkinaFaso;
